import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { NumericFormat } from 'react-number-format';
import {
    toogleLoad,
    setTokenSesion,
    showModalFinSesion
} from "../redux/slices/appslice";
import { getSaldosFecha, getRetirosYSaldos, solicitarRetiro } from "../ws";
import {
    TEXT_FIN_SESION_JWT,
    nWithCommas,
    convertirCamelCase,
    calcularSaldoRealDisponible,
    obtenerCuentasAgrupadas
} from "../utils";
import { guardarAccionBitacora } from "../firestore";
import MaterialReactTable from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { mkConfig, generateCsv, download } from 'export-to-csv';

// Import images/icons
import { ReactComponent as SinRetirosIcon } from '../assets/icons/sin_retiros_icon.svg';
import { ReactComponent as ServicioNoDisponibleIcon } from '../assets/icons/img_servicio_no_disponible.svg';
import { ReactComponent as IconoSolicitudPendiente } from '../assets/icons/icono_solicitud_pendiente.svg';
import ModalEstatusRetiro from '../components/ModalEstatusRetiro';

const TITULOS_MAPA = {
    'fondodeahorro': { titulo: 'Fondo de ahorro', toReturn: '/fondodeahorro' },
    'cajadeahorro': { titulo: 'Caja de ahorro', toReturn: '/cajadeahorro' },
    'plandepensiones': { titulo: 'Fondo de pensiones', toReturn: '/plandepensiones' }
};

function ListaRetiros({ initialTab = 'solicitud' }) {
    const { tipo } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const appData = useSelector((state) => state.appData);
    const appDataRef = useRef(appData);
    appDataRef.current = appData;

    const [activeTab, setActiveTab] = useState(initialTab); // 'solicitud' | 'historial'
    const [paso, setPaso] = useState(1); // 1: Form, 2: Resumen, 3: Exito, 4: Error/Pendiente/Limite, 5: Servicio No Disponible
    
    // Form state
    const [tipoRetiro, setTipoRetiro] = useState("");
    const [monto, setMonto] = useState("");
    const [tipoRetiroError, setTipoRetiroError] = useState(false);
    const [montoError, setMontoError] = useState(false);
    const [montoErrorMsg, setMontoErrorMsg] = useState("");
    const [cuentaClabe, setCuentaClabe] = useState("Cuenta de Depósito");

    // Historial / Data state
    const [data, setData] = useState([]);
    const [todosLosRetiros, setTodosLosRetiros] = useState([]);
    const [titulo, setTitulo] = useState("");
    const [toReturn, setToReturn] = useState("");
    const [error, setError] = useState(false);
    const [saldoMaximo, setSaldoMaximo] = useState(0);
    const [, setCuentasAgrupadas] = useState([]);
    const [errorType, setErrorType] = useState(""); // 'generico', 'pendiente', 'limite'

    // Filtros de UI para historial
    const [anioConsulta, setAnioConsulta] = useState("");
    const [estatusFiltro, setEstatusFiltro] = useState("");
    const [anioConsultado, setAnioConsultado] = useState(new Date().getFullYear().toString());

    // Notificación / Toast state
    const [message, setMessage] = useState(false);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage(false);
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // Modal Estatus Retiro state
    const [modalRetiroOpen, setModalRetiroOpen] = useState(false);
    const [retiroSeleccionado, setRetiroSeleccionado] = useState(null);
    const [retiroIndexSeleccionado, setRetiroIndexSeleccionado] = useState(0);

    const handleOpenModalEstatus = useCallback((retiroRow, rowIndex) => {
        setRetiroSeleccionado(retiroRow);
        setRetiroIndexSeleccionado(rowIndex);
        setModalRetiroOpen(true);
    }, []);

    const getTipoRetiroTexto = useCallback((ret) => {
        if (!ret) return '-';
        if (ret.TipoRetiro && typeof ret.TipoRetiro === 'string' && isNaN(ret.TipoRetiro) && ret.TipoRetiro.trim() !== '') {
            return ret.TipoRetiro.trim();
        }
        const id = (ret.IdTipoRetiro !== undefined && ret.IdTipoRetiro !== null) ? ret.IdTipoRetiro : ret.TipoRetiro;
        if (id !== undefined && id !== null) {
            const found = appData?.configRetiro?.TipoRetiros?.find(t => String(t.IdTipoRetiro) === String(id));
            if (found?.Descripcion) return found.Descripcion.trim();
        }
        if (ret.Descripcion && typeof ret.Descripcion === 'string' && ret.Descripcion.trim() !== '') {
            return ret.Descripcion.trim();
        }
        if (ret.Concepto && typeof ret.Concepto === 'string' && ret.Concepto.trim() !== '') {
            return ret.Concepto.trim();
        }
        return ret.TipoRetiro ? String(ret.TipoRetiro).trim() : '-';
    }, [appData]);

    const getRetiroNumero = (ret, rowIndex) => {
        if (!ret) return 1;
        if (ret.NumeroRetiro) return ret.NumeroRetiro;
        if (ret.IdRetiro) return ret.IdRetiro;
        if (ret.Folio) return ret.Folio;
        if (todosLosRetiros && todosLosRetiros.length > 0) {
            const idxInAll = todosLosRetiros.findIndex(r => r === ret || (r.IdRetiro && r.IdRetiro === ret.IdRetiro) || (r.FechaSolicitud === ret.FechaSolicitud && r.Monto === ret.Monto));
            if (idxInAll !== -1) {
                return todosLosRetiros.length - idxInAll;
            }
        }
        return data.length > 0 ? (data.length - rowIndex) : (rowIndex + 1);
    };

    const handleExportCSV = useCallback(() => {
        const listToExport = data;
        if (!listToExport || listToExport.length === 0) return;

        let clabeUsuario = cuentaClabe;
        if ((!clabeUsuario || clabeUsuario === 'Cuenta de Depósito' || clabeUsuario === 'Sin cuenta registrada') && appData?.user?.Productos) {
            const prodInfo = appData.user.Productos.find(p => p.TipoCliente?.toUpperCase() === titulo.toUpperCase());
            if (prodInfo && prodInfo.Clabe) {
                clabeUsuario = prodInfo.Clabe;
            }
        }

        const csvConfig = mkConfig({
            fieldSeparator: ',',
            decimalSeparator: '.',
            useKeysAsHeaders: true,
            filename: `Retiros_${(titulo || 'Retiros').replace(/ /g, '_')}`,
        });

        const exportData = listToExport.map((row, index) => {
            // Folio / Consecutivo
            const folio = row.IdRetiro || row.Folio || row.NumeroRetiro || (listToExport.length - index);

            // Fecha de Solicitud
            const rawFecha = String(row.FechaSolicitud || row.Fecha || '');
            let fecha = rawFecha;
            if (rawFecha.length === 8 && /^\d{8}$/.test(rawFecha)) {
                fecha = `${rawFecha.substring(6, 8)}/${rawFecha.substring(4, 6)}/${rawFecha.substring(0, 4)}`;
            } else if (rawFecha.includes('-')) {
                const parts = rawFecha.split('T')[0].split('-');
                if (parts.length === 3) {
                    fecha = `${parts[2]}/${parts[1]}/${parts[0]}`;
                }
            }

            // Tipo de retiro
            let tipoRetiroTexto = row.TipoRetiro;
            if (!tipoRetiroTexto || !isNaN(tipoRetiroTexto)) {
                const id = row.IdTipoRetiro || row.TipoRetiro;
                const found = appData?.configRetiro?.TipoRetiros?.find(t => String(t.IdTipoRetiro) === String(id));
                tipoRetiroTexto = found?.Descripcion || row.Descripcion || tipoRetiroTexto || '-';
            }

            // Monto
            const rawMonto = row.Monto;
            const numMonto = typeof rawMonto === 'string' ? parseFloat(rawMonto.replace(/,/g, '')) : (rawMonto || 0);
            const montoStr = !isNaN(numMonto) ? `$ ${nWithCommas(Math.abs(numMonto).toFixed(2))}` : (rawMonto || '-');

            // CLABE
            let clabeFila = row.Clabe;
            if (!clabeFila || clabeFila === "Sin cuenta registrada" || clabeFila === "Cuenta de Depósito") {
                clabeFila = clabeUsuario;
            }
            if (!clabeFila || clabeFila === "Cuenta de Depósito") {
                clabeFila = "Sin cuenta registrada";
            }

            // Estatus
            const rawEstatus = row.Estatus || '';
            const estatusStr = rawEstatus ? (rawEstatus.charAt(0).toUpperCase() + rawEstatus.slice(1).toLowerCase()) : '-';

            const item = {
                'Folio': folio,
                'Fecha de Solicitud': fecha,
                'Tipo de Retiro': tipoRetiroTexto,
                'Monto Solicitado': montoStr,
                'Cuenta CLABE': clabeFila,
                'Estado': estatusStr
            };

            if (row.Cuenta) {
                item['Cuenta'] = row.Cuenta;
            }
            if (row.Descripcion && row.Descripcion !== tipoRetiroTexto) {
                item['Descripción'] = row.Descripcion;
            }

            return item;
        });

        const csv = generateCsv(csvConfig)(exportData);
        download(csvConfig)(csv);

        setMessage({
            texto: '¡Listo! Se ha descargado el reporte en tu dispositivo.',
            tipo: 'exito'
        });
    }, [data, cuentaClabe, appData, titulo]);

    const isWebView = appData.isWebview || false;
    const isFetching = useRef(false);
    const tiempoInicioRef = useRef(location.state?.tiempoInicioSolicitud || Date.now());
    const config = TITULOS_MAPA[tipo];

    const fetchRetirosData = useCallback(async (customConfig = config, showGlobalLoader = true, skipSetData = false) => {
        const targetConfig = customConfig || config;
        if (!targetConfig) return [];
        if (isFetching.current) return [];
        isFetching.current = true;
        if (showGlobalLoader) {
            dispatch(toogleLoad(true));
        }

        try {
            const currentAppData = appDataRef.current;
            const saldoFecha = await getSaldosFecha(currentAppData, () => {
                dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
            });

            if (saldoFecha?.JWT?.current_token) {
                dispatch(setTokenSesion(saldoFecha.JWT.current_token));
            }

            if (!saldoFecha?.BanderaExito) {
                if (showGlobalLoader) setError(true);
                return [];
            }

            const datosList = Array.isArray(saldoFecha?.Datos) ? saldoFecha.Datos : [];
            const prodDato = datosList.find(d =>
                d.Producto?.[0]?.TipoCliente?.toUpperCase() === targetConfig.titulo.toUpperCase()
            );

            if (prodDato?.Producto?.[0]) {
                const prod = prodDato.Producto[0];

                const result = await getRetirosYSaldos(appDataRef.current, prod, () => {
                    dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
                });

                if (result?.JWT?.current_token) {
                    dispatch(setTokenSesion(result.JWT.current_token));
                }

                if (result?.BanderaExito && result.Datos) {
                    let retirosOrdenados = [];
                    if (Array.isArray(result.Datos.Retiros)) {
                        // Sort by FechaSolicitud descending
                        retirosOrdenados = [...result.Datos.Retiros].sort((a, b) => {
                            const fechaA = String(a.FechaSolicitud || '');
                            const fechaB = String(b.FechaSolicitud || '');
                            return fechaB.localeCompare(fechaA);
                        });
                        setTodosLosRetiros(retirosOrdenados);
                        if (!skipSetData) {
                            setData(retirosOrdenados);
                        }
                    } else {
                        setTodosLosRetiros([]);
                        if (!skipSetData) {
                            setData([]);
                        }
                    }

                    const sMax = calcularSaldoRealDisponible(result.Datos);
                    const cAgrupadas = obtenerCuentasAgrupadas(result.Datos);
                    setSaldoMaximo(sMax);
                    setCuentasAgrupadas(cAgrupadas);
                    return retirosOrdenados;
                } else if (showGlobalLoader) {
                    setError(true);
                }
            } else if (showGlobalLoader) {
                setError(true);
            }
            return [];
        } catch (e) {
            console.error("Error fetching retiros:", e);
            if (showGlobalLoader) setError(true);
            return [];
        } finally {
            isFetching.current = false;
            if (showGlobalLoader) {
                dispatch(toogleLoad(false));
            }
        }
    }, [config, dispatch]);

    useEffect(() => {
        if (!config) {
            navigate('/');
            return;
        }

        setTitulo(config.titulo);
        setToReturn(config.toReturn);

        // Extraer Cuenta Clabe
        if (appDataRef.current?.user?.Productos) {
            const prodInfo = appDataRef.current.user.Productos.find(p => p.TipoCliente?.toUpperCase() === config.titulo.toUpperCase());
            if (prodInfo && prodInfo.Clabe) {
                setCuentaClabe(prodInfo.Clabe);
            }
        }

        fetchRetirosData(config, true);
    }, [config, navigate, fetchRetirosData]);

    const handleContinuar = () => {
        let hasError = false;
        
        if (!tipoRetiro) {
            setTipoRetiroError(true);
            hasError = true;
        } else {
            setTipoRetiroError(false);
        }

        const montoNum = parseFloat(monto || 0);
        if (!monto || montoNum <= 0) {
            setMontoError(true);
            setMontoErrorMsg("Ingresa un importe válido.");
            hasError = true;
        } else {
            setMontoError(false);
            setMontoErrorMsg("");
        }

        if (!hasError) {
            setPaso(2); // Go to Resumen
        }
    };

    const submitRetiro = async () => {
        dispatch(toogleLoad(true));
        try {
            const resultSolicitudRetiro = await solicitarRetiro(appData, { tipoRetiro, monto }, () => {
                dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
            });
            
            if (resultSolicitudRetiro) {
                if (resultSolicitudRetiro.BanderaExito === 0 && resultSolicitudRetiro.Mensaje?.trim() !== "") {
                    const msgUpper = resultSolicitudRetiro.Mensaje.toUpperCase();
                    if (msgUpper.includes("PENDIENTE") || msgUpper.includes("ESPERA")) {
                        setErrorType('pendiente');
                    } else if (msgUpper.includes("LÍMITE") || msgUpper.includes("MAXIMO") || msgUpper.includes("MÁXIMO")) {
                        setErrorType('limite');
                    } else {
                        setErrorType('generico');
                    }
                    setPaso(4);
                } else {
                    const duracionSegundos = Math.round((Date.now() - tiempoInicioRef.current) / 1000);
                    await guardarAccionBitacora(
                        "SolicitudRetiroExitosa",
                        `Solicitud de retiro de $${nWithCommas(parseFloat(monto).toFixed(2))} completada en ${duracionSegundos} segundos`,
                        "SUCCESS",
                        appData.user?.usuarioLogin || appData.user?.IdUsuario
                    );
                    setPaso(3); // Éxito
                }
            } else {
                setPaso(5); // Servicio no disponible
            }
        } catch (error) {
            setPaso(5);
        } finally {
            dispatch(toogleLoad(false));
        }
    };

    const canConsultar = Boolean(anioConsulta && estatusFiltro);

    const extractAnioFromFecha = (fechaRaw) => {
        if (!fechaRaw) return null;
        const str = String(fechaRaw).trim();
        // Formato YYYYMMDD (8 dígitos numéricos)
        if (str.length === 8 && /^\d{8}$/.test(str)) {
            return str.substring(0, 4);
        }
        // Formato YYYY-MM-DD o YYYY/MM/DD
        if (/^\d{4}[-/]/.test(str)) {
            return str.substring(0, 4);
        }
        // Año de 4 dígitos en cualquier parte (ej. 22/12/2020)
        const match4 = str.match(/\b(20\d{2})\b/);
        if (match4) return match4[1];
        // Año de 2 dígitos al final (ej. 22/12/20 o 22-12-20)
        const match2 = str.match(/[-/](\d{2})$/);
        if (match2) {
            return `20${match2[1]}`;
        }
        return null;
    };

    const matchAnio = useCallback((fecha, anio) => {
        if (!anio || String(anio).toLowerCase() === 'todos') return true;
        if (!fecha) return false;
        const anioStr = String(anio).trim();
        const extracted = extractAnioFromFecha(fecha);
        if (extracted && extracted === anioStr) {
            return true;
        }
        const str = String(fecha).trim();
        const anio2Dig = anioStr.length === 4 ? anioStr.substring(2, 4) : anioStr;
        if (str.includes(anioStr) || str.endsWith('/' + anio2Dig) || str.endsWith('-' + anio2Dig)) {
            return true;
        }
        return false;
    }, []);

    const matchEstatus = (retiro, filtroVal) => {
        if (!filtroVal || String(filtroVal).toLowerCase() === 'todos') return true;
        const filtroUpper = String(filtroVal).trim().toUpperCase();
        const retUpper = String(retiro.Estatus || '').trim().toUpperCase();

        if (!retUpper) return false;
        if (retUpper === filtroUpper) return true;

        // Equivalencias si el estatus en backend varía ligeramente (Autorizado vs Aplicado)
        if ((filtroUpper === 'AUTORIZADO' || filtroUpper === 'APLICADO') &&
            (retUpper === 'AUTORIZADO' || retUpper === 'APLICADO')) {
            return true;
        }
        // Pendiente vs Por autorizar vs En proceso / Recibido
        if ((filtroUpper.includes('PENDIENTE') || filtroUpper.includes('POR AUTORIZAR') || filtroUpper.includes('PROCESO') || filtroUpper.includes('RECIBIDO')) &&
            (retUpper.includes('PENDIENTE') || retUpper.includes('POR AUTORIZAR') || retUpper.includes('PROCESO') || retUpper.includes('RECIBIDO'))) {
            return true;
        }
        // Cancelado vs Rechazado
        if ((filtroUpper.includes('CANCELADO') || filtroUpper.includes('RECHAZADO')) &&
            (retUpper.includes('CANCELADO') || retUpper.includes('RECHAZADO'))) {
            return true;
        }

        return retUpper.includes(filtroUpper) || filtroUpper.includes(retUpper);
    };

    const handleConsultar = async () => {
        if (!canConsultar) return;

        const retirosActualizados = await fetchRetirosData(config, true, true);
        const sourceList = (Array.isArray(retirosActualizados) && retirosActualizados.length > 0)
            ? retirosActualizados
            : todosLosRetiros;

        const filtrados = sourceList.filter((item) => {
            const matchesYear = matchAnio(item.FechaSolicitud || item.Fecha, anioConsulta);
            const matchesStatus = matchEstatus(item, estatusFiltro);
            return matchesYear && matchesStatus;
        });

        setData(filtrados);
        setAnioConsultado(anioConsulta.toLowerCase() === 'todos' ? 'Todos' : anioConsulta);
    };

    useEffect(() => {
        if (!anioConsulta && !estatusFiltro && todosLosRetiros.length > 0) {
            setData(todosLosRetiros);
            setAnioConsultado(new Date().getFullYear().toString());
        }
    }, [anioConsulta, estatusFiltro, todosLosRetiros]);

    const opcionesAnios = useMemo(() => {
        const yearsSet = new Set(["2026", "2025", "2024", "2023"]);
        if (Array.isArray(todosLosRetiros)) {
            todosLosRetiros.forEach((item) => {
                const year = extractAnioFromFecha(item.FechaSolicitud || item.Fecha);
                if (year) {
                    yearsSet.add(year);
                }
            });
        }
        return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
    }, [todosLosRetiros]);

    const estatusRetiroDisponibles = useMemo(() => {
        let retirosBase = [];
        if (Array.isArray(todosLosRetiros)) {
            if (!anioConsulta || String(anioConsulta).toLowerCase() === 'todos') {
                retirosBase = todosLosRetiros;
            } else {
                retirosBase = todosLosRetiros.filter((item) =>
                    matchAnio(item.FechaSolicitud || item.Fecha, anioConsulta)
                );
            }
        }

        const map = new Map();

        retirosBase.forEach((item) => {
            const raw = item.Estatus;
            if (raw && String(raw).trim() !== '' && String(raw).trim() !== '-') {
                const s = String(raw).trim();
                const formatted = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
                const key = formatted.toLowerCase();
                if (!map.has(key)) {
                    map.set(key, {
                        valor: formatted,
                        descripcion: formatted
                    });
                }
            }
        });

        if (map.size === 0) {
            return [
                { valor: 'Pendiente', descripcion: 'Pendiente' },
                { valor: 'Autorizado', descripcion: 'Autorizado' },
                { valor: 'Cancelado', descripcion: 'Cancelado' }
            ];
        }

        return Array.from(map.values()).sort((a, b) => 
            a.descripcion.localeCompare(b.descripcion, 'es', { sensitivity: 'base' })
        );
    }, [todosLosRetiros, anioConsulta, matchAnio]);

    useEffect(() => {
        if (!estatusFiltro || estatusFiltro === "Todos") return;
        const exists = estatusRetiroDisponibles.some(
            (t) => String(t.valor).toLowerCase() === String(estatusFiltro).toLowerCase() || 
                   String(t.descripcion).toLowerCase() === String(estatusFiltro).toLowerCase()
        );
        if (!exists) {
            setEstatusFiltro("");
        }
    }, [estatusRetiroDisponibles, estatusFiltro]);

    const getSortIcon = (column) => {
        const isSorted = column.getIsSorted();
        if (isSorted === 'desc') return 'arrow_downward';
        if (isSorted === 'asc') return 'arrow_upward';
        return 'unfold_more';
    };

    const renderHeader = (label, column) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <span>{label}</span>
            <i className="material-icons" style={{ fontSize: '16px', marginLeft: '4px', color: column.getIsSorted() ? '#202A52' : '#9e9e9e' }}>
                {getSortIcon(column)}
            </i>
        </div>
    );

    const columns = useMemo(() => [
        {
            accessorKey: 'FechaSolicitud',
            header: 'Fecha de Solicitud',
            Header: ({ column }) => renderHeader('Fecha de Solicitud', column),
            Cell: ({ cell }) => {
                const raw = cell.getValue();
                if (!raw) return '-';
                const str = String(raw);
                if (str.length === 8) {
                    return `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(2, 4)}`;
                }
                return str;
            }
        },
        {
            accessorKey: 'TipoRetiro',
            header: 'Tipo de retiro',
            Header: ({ column }) => renderHeader('Tipo de retiro', column),
            Cell: ({ row, cell }) => getTipoRetiroTexto(row.original) || cell.getValue() || '-'
        },
        {
            accessorKey: 'Monto',
            header: 'Monto Solicitado',
            Header: ({ column }) => renderHeader('Monto Solicitado', column),
            Cell: ({ cell }) => {
                const val = cell.getValue();
                if (val === undefined || val === null) return '-';
                const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
                return `$ ${nWithCommas(Math.abs(num).toFixed(2))}`;
            }
        },
        {
            accessorKey: 'Clabe',
            header: 'Cuenta Clabe',
            Header: ({ column }) => renderHeader('Cuenta Clabe', column),
            Cell: ({ row }) => {
                let clabe = "Sin cuenta registrada";
                if (appData?.user?.Productos) {
                    const prodInfo = appData.user.Productos.find(p => p.TipoCliente?.toUpperCase() === titulo.toUpperCase());
                    if (prodInfo && prodInfo.Clabe) {
                        clabe = prodInfo.Clabe;
                    }
                }
                return clabe;
            }
        },
        {
            accessorKey: 'Estatus',
            header: 'Estado',
            Header: ({ column }) => renderHeader('Estado', column),
            Cell: ({ cell, row }) => {
                const status = cell.getValue();
                if (!status) return '-';
                
                let bgColor = '#e0e0e0';
                let textColor = 'white';
                const sUpper = status.toUpperCase();
                
                if (sUpper === 'AUTORIZADO' || sUpper === 'APLICADO') {
                    bgColor = '#6CBE6C'; 
                } else if (
                    sUpper.includes('PENDIENTE') ||
                    sUpper.includes('POR AUTORIZAR') || 
                    sUpper.includes('PROCESO') || 
                    sUpper.includes('RECIBIDO')
                ) {
                    bgColor = '#F9B400'; 
                } else if (sUpper === 'CANCELADO' || sUpper.includes('RECHAZADO')) {
                    bgColor = '#e57373'; 
                }

                return (
                    <div 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModalEstatus(row.original, row.index);
                        }}
                        style={{
                            backgroundColor: bgColor,
                            color: textColor,
                            padding: '4px 12px',
                            borderRadius: '16px',
                            display: 'inline-block',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                        title="Ver detalle del estatus"
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                    </div>
                );
            }
        }
    ], [appData, titulo, handleOpenModalEstatus, getTipoRetiroTexto]);

    if (error) {
        return (
            <div className="container" style={{ marginTop: '2rem', minHeight: 'calc(100vh - 120px)', paddingBottom: '3rem' }}>
                <div className="row">
                    <div className="col s12 left-align">
                        {toReturn && !isWebView && (
                            <p className="cursorPointer" onClick={() => navigate(toReturn)} style={{ color: '#64748b', margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
                                <i className="material-icons" style={{ fontSize: '18px', marginRight: '4px' }}>arrow_back</i> {titulo}
                            </p>
                        )}
                        <h4 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700 }}>Retiros</h4>
                    </div>
                </div>
                <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', marginTop: '1rem', backgroundColor: 'white' }}>
                    <ServicioNoDisponibleIcon style={{ maxWidth: '100%', height: 'auto', margin: '2rem 0' }} />
                    <p style={{ color: '#666' }}>Ocurrió un error al procesar tu solicitud. Verifica los datos e intenta nuevamente.</p>
                    <p style={{ color: '#666' }}>Si el problema continúa, contáctanos en <b>solucionesempleados@valmex.mx</b> o al <b>5800 0082 563.</b></p>
                    <button 
                        className="btn" 
                        style={{ backgroundColor: '#3b4566', color: 'white', marginTop: '2rem', textTransform: 'none', borderRadius: '8px' }}
                        onClick={() => navigate(toReturn)}
                    >
                        Entendido
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container" style={{ marginTop: '2rem', maxWidth: '1100px', minHeight: 'calc(100vh - 120px)', paddingBottom: '3rem' }}>
            <div className="row">
                <div className="col s12">
                    {/* Mensaje de éxito al descargar reporte */}
                    {message && (
                        <div 
                            style={{
                                backgroundColor: '#86CB7B',
                                color: 'white',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '1.5rem',
                                fontSize: '15px',
                                fontWeight: 700,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: '12px', flexShrink: 0 }}>
                                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
                                <path d="M8 12.5l2.5 2.5L16 9.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                            <span className="boldText" style={{ fontWeight: 700 }}>{message.texto || '¡Listo! Se ha descargado el reporte en tu dispositivo.'}</span>
                        </div>
                    )}

                    {/* Header común: Breadcrumb, Título "Retiros" y Botón "✕ Cerrar" */}
                    <div style={{ marginBottom: '1.5rem' }}>
                {toReturn && !isWebView && (
                    <p 
                        className="cursorPointer" 
                        onClick={() => navigate(toReturn)} 
                        style={{ color: '#64748b', margin: '0 0 8px 0', display: 'inline-flex', alignItems: 'center', fontSize: '14px' }}
                    >
                        <i className="material-icons" style={{ fontSize: '18px', marginRight: '4px' }}>arrow_back</i> {titulo}
                    </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <h4 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '32px' }}>
                        Retiros
                    </h4>
                    <button 
                        className="btn white" 
                        onClick={() => navigate(toReturn)}
                        style={{ 
                            color: '#202A52', 
                            border: '1px solid #cbd5e1', 
                            borderRadius: '8px', 
                            textTransform: 'none', 
                            boxShadow: 'none', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            fontWeight: 600, 
                            padding: '0 16px', 
                            height: '38px',
                            fontSize: '14px',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>close</i> Cerrar
                    </button>
                </div>
            </div>

            {/* Subtítulo orientativo */}
            <p style={{ color: '#202A52', fontSize: '16px', fontWeight: 600, margin: '0 0 12px 0' }}>
                Selecciona la consulta que deseas realizar
            </p>

            {/* Barra de pestañas */}
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0 24px',
                display: 'flex',
                gap: '32px',
                alignItems: 'center',
                marginBottom: '2rem',
                height: '56px'
            }}>
                <div 
                    onClick={() => {
                        setActiveTab('solicitud');
                        if (paso > 2) setPaso(1);
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        height: '100%',
                        borderBottom: activeTab === 'solicitud' ? '3px solid #202A52' : '3px solid transparent',
                        color: activeTab === 'solicitud' ? '#202A52' : '#94a3b8',
                        fontWeight: activeTab === 'solicitud' ? '700' : '500',
                        padding: '0 4px',
                        fontSize: '15px'
                    }}
                >
                    <i className="material-icons" style={{ fontSize: '20px', marginRight: '8px', color: activeTab === 'solicitud' ? '#202A52' : '#94a3b8' }}>payments</i>
                    <span>Solicitud de retiro</span>
                </div>

                <div 
                    onClick={() => {
                        setActiveTab('historial');
                        setAnioConsulta("");
                        setEstatusFiltro("");
                        if (config) {
                            fetchRetirosData(config, true);
                        }
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        height: '100%',
                        borderBottom: activeTab === 'historial' ? '3px solid #202A52' : '3px solid transparent',
                        color: activeTab === 'historial' ? '#202A52' : '#94a3b8',
                        fontWeight: activeTab === 'historial' ? '700' : '500',
                        padding: '0 4px',
                        fontSize: '15px'
                    }}
                >
                    <i className="material-icons" style={{ fontSize: '20px', marginRight: '8px', color: activeTab === 'historial' ? '#202A52' : '#94a3b8' }}>history</i>
                    <span>Historial de retiros</span>
                </div>
            </div>

            {/* CONTENIDO PESTAÑA 1: SOLICITUD DE RETIRO */}
            {activeTab === 'solicitud' && (
                <div>
                    {paso === 5 && (
                        /* Paso 5: Error 500 / Servicio no disponible */
                        <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <h5 className="txtBold" style={{ color: '#202A52' }}>¡Servicio no disponible!</h5>
                            <ServicioNoDisponibleIcon style={{ maxWidth: '300px', height: 'auto', margin: '2rem 0' }} />
                            <p style={{ color: '#666' }}>Ocurrió un error al procesar tu solicitud. Verifica los datos e intenta nuevamente.</p>
                            <p style={{ color: '#666' }}>Si el problema continúa, contáctanos en <b>solucionesempleados@valmex.mx</b> o al <b>5800 0082 563.</b></p>
                            <button 
                                className="btn" 
                                style={{ backgroundColor: '#3b4566', color: 'white', marginTop: '2rem', textTransform: 'none', borderRadius: '8px' }}
                                onClick={() => setPaso(1)}
                            >
                                Entendido
                            </button>
                        </div>
                    )}

                    {paso === 4 && (
                        /* Paso 4: Errores de lógica de negocio (Pendiente, Límite, Genérico) */
                        <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <h5 className="txtBold" style={{ color: '#202A52' }}>
                                {errorType === 'pendiente' ? "¡Solicitud de retiro pendiente!" : errorType === 'limite' ? "¡Límite de retiros alcanzado!" : "¡Tu solicitud no pudo ser procesada!"}
                            </h5>
                            <IconoSolicitudPendiente style={{ maxWidth: '300px', height: 'auto', margin: '2rem 0' }} />
                            <div style={{ color: '#666', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
                                {errorType === 'pendiente' && "Tienes una solicitud de retiro en espera de aprobación. Cuando sea autorizada, podrás realizar un nuevo retiro."}
                                {errorType === 'limite' && "Has alcanzado el número máximo de retiros permitidos."}
                                {errorType === 'generico' && (
                                    <>
                                        <p className="mb-0">Verifica los datos e intenta nuevamente. Si el problema persiste</p>   
                                        <p className="mt-0 mb-0">contáctanos en <b>solucionesempleados@valmex.mx</b></p>
                                        <p className="mt-0">o al <b>(52) 55 5279 1433.</b></p>
                                    </>
                                )}
                            </div>
                            <button 
                                className="btn" 
                                style={{ backgroundColor: '#3b4566', color: 'white', marginTop: '2rem', textTransform: 'none', borderRadius: '8px' }}
                                onClick={() => setPaso(1)}
                            >
                                Entendido
                            </button>
                        </div>
                    )}

                    {paso === 3 && (
                        /* Paso 3: Éxito */
                        <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <h5 className="txtBold" style={{ color: '#202A52' }}>¡Tu solicitud ha sido enviada!</h5>
                            <IconoSolicitudPendiente style={{ maxWidth: '300px', height: 'auto', margin: '2rem 0' }} />
                            <p style={{ color: '#666', fontSize: '16px', maxWidth: '400px', margin: '0 auto' }}>
                                Tu solicitud de retiro de efectivo esta en proceso de autorización.
                            </p>
                            <button 
                                className="btn" 
                                style={{ backgroundColor: '#3b4566', color: 'white', marginTop: '2rem', textTransform: 'none', borderRadius: '8px' }}
                                onClick={() => {
                                    setPaso(1);
                                    setTipoRetiro("");
                                    setMonto("");
                                    setAnioConsulta("");
                                    setEstatusFiltro("");
                                    setActiveTab('historial');
                                    if (config) {
                                        fetchRetirosData(config, true);
                                    }
                                }}
                            >
                                Entendido
                            </button>
                        </div>
                    )}

                    {paso === 2 && (
                        /* Paso 2: Resumen y Confirmación */
                        <div style={{ maxWidth: '620px', margin: '0 auto' }}>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '2.5rem 2rem', textAlign: 'center', backgroundColor: 'white' }}>
                                <h5 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '20px' }}>
                                    Confirma el monto solicitado
                                </h5>
                                <p style={{ color: '#94a3b8', margin: '8px 0 2rem 0', fontSize: '14px' }}>
                                    Verifica que la cantidad a solicitar sea correcta.
                                </p>
                                
                                <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Monto solicitado</p>
                                <h3 className="txtBold" style={{ color: '#202A52', margin: '4px 0 2rem 0', fontSize: '32px' }}>
                                    $ {nWithCommas(parseFloat(monto).toFixed(2))}
                                </h3>

                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
                                    <span style={{ color: '#94a3b8' }}>Tipo de retiro</span>
                                    <span className="txtBold" style={{ color: '#202A52' }}>
                                        {tipoRetiro ? convertirCamelCase(appData.configRetiro?.TipoRetiros?.find(v => v.IdTipoRetiro === parseInt(tipoRetiro))?.Descripcion || '') : ''}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                                    <span style={{ color: '#94a3b8' }}>Cuenta Clabe</span>
                                    <span className="txtBold" style={{ color: '#202A52' }}>{cuentaClabe.replace(/.(?=.{4})/g, '*')}</span>
                                </div>

                                <div style={{ backgroundColor: '#e0f2f1', borderRadius: '8px', padding: '1rem', display: 'flex', alignItems: 'flex-start', textAlign: 'left', marginBottom: '2rem' }}>
                                    <i className="material-icons" style={{ color: '#00897b', marginRight: '8px' }}>info_outline</i>
                                    <span style={{ color: '#666', fontSize: '13px' }}>
                                        De ser aprobada tu solicitud, el depósito se realizará en la cuenta bancaria registrada.
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                                    <button 
                                        className="btn white"
                                        onClick={() => setPaso(1)}
                                        style={{ color: '#3b4566', border: '1px solid #cbd5e1', textTransform: 'none', borderRadius: '8px', boxShadow: 'none', height: '44px', fontWeight: 600 }}
                                    >
                                        <i className="material-icons left" style={{ fontSize: '18px' }}>arrow_back</i> Regresar
                                    </button>
                                    <button 
                                        className="btn"
                                        onClick={submitRetiro}
                                        style={{ backgroundColor: '#3b4566', color: 'white', textTransform: 'none', borderRadius: '8px', height: '44px', padding: '0 2rem', fontWeight: 600, boxShadow: 'none' }}
                                    >
                                        Solicitar retiro
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {paso === 1 && (
                        /* Paso 1: Formulario idéntico a maqueta PESTANIA_SOLICITUD.png */
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '3rem 2rem',
                            maxWidth: '620px',
                            margin: '0 auto'
                        }}>
                            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                                <h5 className="txtBold" style={{ color: '#202A52', margin: '0 0 8px 0', fontWeight: 700, fontSize: '22px' }}>
                                    Retiro {titulo.toLowerCase()}
                                </h5>
                                <p style={{ color: '#94a3b8', margin: 0, fontSize: '14px' }}>
                                    Ingresa la siguiente información para enviar tu solicitud.
                                </p>
                            </div>

                            <div style={{ maxWidth: '440px', margin: '0 auto' }}>
                                {/* Tipo de retiro */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                        Tipo de retiro
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <select 
                                            className="browser-default" 
                                            value={tipoRetiro} 
                                            onChange={(e) => {
                                                setTipoRetiro(e.target.value);
                                                setTipoRetiroError(false);
                                            }}
                                            style={{ 
                                                width: '100%',
                                                height: '46px',
                                                backgroundColor: tipoRetiroError ? '#ffebee' : '#f8fafc', 
                                                border: tipoRetiroError ? '1px solid #ef5350' : 'none', 
                                                borderBottom: tipoRetiroError ? '2px solid #ef5350' : '2px solid #00897b', 
                                                borderRadius: '6px 6px 0 0',
                                                padding: '0 2.5rem 0 1rem', 
                                                color: tipoRetiro ? '#202A52' : '#94a3b8',
                                                fontSize: '14px',
                                                outline: 'none',
                                                appearance: 'none'
                                            }}
                                        >
                                            <option value="" disabled>-Selecciona un tipo-</option>
                                            {appData.configRetiro && appData.configRetiro.TipoRetiros && appData.configRetiro.TipoRetiros.map((v, i) => (
                                                <option key={i} value={v.IdTipoRetiro} style={{ color: '#202A52' }}>{v.Descripcion}</option>
                                            ))}
                                        </select>
                                        <i className="material-icons" style={{ position: 'absolute', right: '12px', top: '12px', pointerEvents: 'none', color: '#202A52' }}>expand_more</i>
                                    </div>
                                    {tipoRetiroError && <span style={{ color: '#ef5350', fontSize: '12px', marginTop: '4px', display: 'block' }}>Selecciona una opción de tipo de retiro</span>}
                                </div>

                                {/* Monto solicitado */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                        Monto solicitado
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <NumericFormat
                                            thousandSeparator={','}
                                            decimalSeparator="."
                                            value={monto}
                                            isNumericString
                                            decimalScale={2}
                                            fixedDecimalScale
                                            placeholder="Ej: 10.000.00"
                                            prefix="$ "
                                            onValueChange={(values) => {
                                                const valStr = values.value;
                                                setMonto(valStr);
                                                
                                                if (valStr !== "") {
                                                    const montoNum = parseFloat(valStr || 0);
                                                    if (montoNum <= 0) {
                                                        setMontoError(true);
                                                        setMontoErrorMsg("Ingresa un importe válido.");
                                                    } else {
                                                        setMontoError(false);
                                                        setMontoErrorMsg("");
                                                    }
                                                } else {
                                                    setMontoError(false);
                                                    setMontoErrorMsg("");
                                                }
                                            }}
                                            style={{ 
                                                width: '100%',
                                                height: '46px',
                                                backgroundColor: montoError ? '#ffebee' : '#f8fafc', 
                                                border: montoError ? '1px solid #ef5350' : 'none', 
                                                borderBottom: montoError ? '2px solid #ef5350' : '2px solid #00897b', 
                                                borderRadius: '6px 6px 0 0',
                                                padding: '0 2.5rem 0 1rem', 
                                                color: '#202A52',
                                                fontSize: '14px',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        {montoError && <i className="material-icons" style={{ position: 'absolute', right: '10px', top: '10px', color: '#ef5350' }}>error</i>}
                                    </div>
                                    {montoError && <span style={{ color: '#ef5350', fontSize: '12px', marginTop: '4px', display: 'block' }}>{montoErrorMsg}</span>}
                                </div>

                                {/* Cuenta clave */}
                                <div style={{ marginBottom: '2.5rem' }}>
                                    <label style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                        Cuenta clave
                                    </label>
                                    <input 
                                        disabled 
                                        value={cuentaClabe.replace(/.(?=.{4})/g, '*')}
                                        style={{ 
                                            width: '100%',
                                            height: '46px',
                                            backgroundColor: '#cfd8dc', 
                                            border: 'none', 
                                            borderBottom: '2px solid #94a3b8', 
                                            borderRadius: '6px 6px 0 0',
                                            padding: '0 1rem', 
                                            color: '#475569',
                                            fontSize: '14px',
                                            boxSizing: 'border-box'
                                        }} 
                                    />
                                </div>

                                {/* Boton Continuar */}
                                <div style={{ textAlign: 'center' }}>
                                    <button 
                                        className="btn"
                                        onClick={handleContinuar}
                                        style={{ 
                                            backgroundColor: (tipoRetiro && monto && parseFloat(monto) > 0) ? '#3b4566' : '#b0bec5', 
                                            color: 'white', 
                                            textTransform: 'none', 
                                            borderRadius: '8px', 
                                            width: '220px', 
                                            height: '44px',
                                            boxShadow: 'none',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            cursor: (tipoRetiro && monto && parseFloat(monto) > 0) ? 'pointer' : 'default'
                                        }}
                                    >
                                        Continuar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CONTENIDO PESTAÑA 2: HISTORIAL DE RETIROS */}
            {activeTab === 'historial' && (
                <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '2.5rem',
                    width: '100%'
                }}>
                    <h5 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '20px' }}>
                        Consulta tu historial
                    </h5>
                    <p style={{ color: '#94a3b8', margin: '4px 0 2rem 0', fontSize: '14px' }}>
                        Selecciona el año y el estatus del retiro para ver el detalle.
                    </p>

                    {/* Fila de Filtros visuales */}
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '2rem' }}>
                        <div style={{ flex: '1', minWidth: '220px' }}>
                            <label style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                Año de consulta
                            </label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    className="browser-default"
                                    value={anioConsulta}
                                    onChange={(e) => setAnioConsulta(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '44px',
                                        backgroundColor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        padding: '0 2.5rem 0 1rem',
                                        color: anioConsulta ? '#202A52' : '#94a3b8',
                                        fontSize: '14px',
                                        outline: 'none',
                                        appearance: 'none'
                                    }}
                                >
                                    <option value="">Selecciona una opción</option>
                                    <option value="Todos" style={{ color: '#202A52' }}>Todos</option>
                                    {opcionesAnios.map((anio) => (
                                        <option key={anio} value={anio} style={{ color: '#202A52' }}>{anio}</option>
                                    ))}
                                </select>
                                <i className="material-icons" style={{ position: 'absolute', right: '12px', top: '10px', pointerEvents: 'none', color: '#202A52' }}>expand_more</i>
                            </div>
                        </div>

                        <div style={{ flex: '1', minWidth: '220px' }}>
                            <label style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                Estado del retiro
                            </label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    className="browser-default"
                                    value={estatusFiltro}
                                    onChange={(e) => setEstatusFiltro(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '44px',
                                        backgroundColor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        padding: '0 2.5rem 0 1rem',
                                        color: estatusFiltro ? '#202A52' : '#94a3b8',
                                        fontSize: '14px',
                                        outline: 'none',
                                        appearance: 'none'
                                    }}
                                >
                                    <option value="">Selecciona una opción</option>
                                    <option value="Todos" style={{ color: '#202A52' }}>Todos</option>
                                    {estatusRetiroDisponibles.map((v, i) => (
                                        <option key={i} value={v.valor} style={{ color: '#202A52' }}>{v.descripcion}</option>
                                    ))}
                                </select>
                                <i className="material-icons" style={{ position: 'absolute', right: '12px', top: '10px', pointerEvents: 'none', color: '#202A52' }}>expand_more</i>
                            </div>
                        </div>

                        <div>
                            <button 
                                className="btn"
                                disabled={!canConsultar}
                                onClick={handleConsultar}
                                style={{
                                    backgroundColor: canConsultar ? '#3b4566' : '#b0bec5',
                                    color: 'white',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    height: '44px',
                                    padding: '0 2rem',
                                    boxShadow: 'none',
                                    fontWeight: 600,
                                    cursor: canConsultar ? 'pointer' : 'default'
                                }}
                            >
                                Consultar
                            </button>
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                            <div>
                                <h6 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '18px' }}>
                                    Retiros del año
                                </h6>
                                <span style={{ color: '#64748b', fontSize: '14px' }}>{anioConsultado || new Date().getFullYear()}</span>
                            </div>
                            <button 
                                type="button"
                                className="btn white" 
                                onClick={handleExportCSV}
                                disabled={!data || data.length === 0}
                                style={{
                                    color: '#202A52',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '38px',
                                    padding: '0 16px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: (data && data.length > 0) ? 'pointer' : 'default',
                                    opacity: (data && data.length > 0) ? 1 : 0.6
                                }}
                                title="Descargar información de retiros en CSV"
                            >
                                <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>description</i> Descargar CSV
                            </button>
                        </div>

                        {/* Tabla intacta de MaterialReactTable proveniente de ListaRetiros.js */}
                        {data.length === 0 ? (
                            <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', marginTop: '1rem' }}>
                                <SinRetirosIcon style={{ maxWidth: '100%', height: 'auto', margin: '2rem 0' }} />
                                <p style={{ color: '#666', fontSize: '18px' }}>Aún no tienes retiros registrados</p>
                            </div>
                        ) : (
                            <MaterialReactTable
                                columns={columns}
                                data={data}
                                enableColumnFilters={false}
                                enableDensityToggle={false}
                                enableFullScreenToggle={false}
                                enablePagination={true}
                                enableSorting={true}
                                enableColumnActions={false}
                                enableTopToolbar={false}
                                enableBottomToolbar={true}
                                initialState={{
                                    pagination: { pageSize: 6, pageIndex: 0 }
                                }}
                                renderBottomToolbar={({ table }) => {
                                    const pageIndex = table.getState().pagination.pageIndex;
                                    const pageCount = Math.max(table.getPageCount(), 1);
                                    const canPrev = table.getCanPreviousPage();
                                    const canNext = table.getCanNextPage();

                                    return (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '1.25rem 0',
                                            gap: '8px',
                                            borderTop: '1px solid #e0e0e0',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '0 0 8px 8px'
                                        }}>
                                            {/* Primera página: << */}
                                            <button
                                                type="button"
                                                disabled={!canPrev}
                                                onClick={() => table.setPageIndex(0)}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                    border: canPrev ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                    color: canPrev ? '#202A52' : '#cbd5e1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: canPrev ? 'pointer' : 'default',
                                                    outline: 'none',
                                                    padding: 0,
                                                    boxShadow: 'none',
                                                    transition: 'all 0.2s'
                                                }}
                                                title="Primera página"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="11 17 6 12 11 7" />
                                                    <polyline points="18 17 13 12 18 7" />
                                                </svg>
                                            </button>

                                            {/* Página anterior: < */}
                                            <button
                                                type="button"
                                                disabled={!canPrev}
                                                onClick={() => table.previousPage()}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                    border: canPrev ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                    color: canPrev ? '#202A52' : '#cbd5e1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: canPrev ? 'pointer' : 'default',
                                                    outline: 'none',
                                                    padding: 0,
                                                    boxShadow: 'none',
                                                    transition: 'all 0.2s'
                                                }}
                                                title="Página anterior"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="15 18 9 12 15 6" />
                                                </svg>
                                            </button>

                                            {/* Texto Página X de Y */}
                                            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, margin: '0 16px' }}>
                                                Página {pageIndex + 1} de {pageCount}
                                            </span>

                                            {/* Página siguiente: > */}
                                            <button
                                                type="button"
                                                disabled={!canNext}
                                                onClick={() => table.nextPage()}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                    border: canNext ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                    color: canNext ? '#202A52' : '#cbd5e1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: canNext ? 'pointer' : 'default',
                                                    outline: 'none',
                                                    padding: 0,
                                                    boxShadow: 'none',
                                                    transition: 'all 0.2s'
                                                }}
                                                title="Página siguiente"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </button>

                                            {/* Última página: >> */}
                                            <button
                                                type="button"
                                                disabled={!canNext}
                                                onClick={() => table.setPageIndex(pageCount - 1)}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                    border: canNext ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                    color: canNext ? '#202A52' : '#cbd5e1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: canNext ? 'pointer' : 'default',
                                                    outline: 'none',
                                                    padding: 0,
                                                    boxShadow: 'none',
                                                    transition: 'all 0.2s'
                                                }}
                                                title="Última página"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="13 17 18 12 13 7" />
                                                    <polyline points="6 17 11 12 6 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    );
                                }}
                                muiTablePaperProps={{
                                    elevation: 0,
                                    style: { border: '1px solid #e0e0e0', borderRadius: '8px' }
                                }}
                                muiTableHeadCellProps={{
                                    sx: {
                                        backgroundColor: '#F6F8FA',
                                        color: '#666',
                                        fontWeight: 'bold',
                                        borderBottom: '1px solid #e0e0e0',
                                        '& .MuiTableSortLabel-icon': {
                                            display: 'none',
                                        }
                                    }
                                }}
                                localization={{
                                    ...MRT_Localization_ES,
                                    noRecordsToDisplay: 'No hay retiros para mostrar'
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            <ModalEstatusRetiro
                isOpen={modalRetiroOpen}
                onClose={() => setModalRetiroOpen(false)}
                retiro={retiroSeleccionado}
                index={getRetiroNumero(retiroSeleccionado, retiroIndexSeleccionado)}
                clabe={(() => {
                    if (retiroSeleccionado?.Clabe) return retiroSeleccionado.Clabe;
                    if (cuentaClabe && cuentaClabe !== "Cuenta de Depósito" && cuentaClabe !== "Sin cuenta registrada") return cuentaClabe;
                    if (appData?.user?.Productos) {
                        const prodInfo = appData.user.Productos.find(p => p.TipoCliente?.toUpperCase() === titulo.toUpperCase());
                        if (prodInfo && prodInfo.Clabe) return prodInfo.Clabe;
                    }
                    return "";
                })()}
                tipoRetiroTexto={getTipoRetiroTexto(retiroSeleccionado)}
            />
                </div>
            </div>
        </div>
    );
}

export default ListaRetiros;
